import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { ChevronLeft, ChevronRight, EllipsisVertical, List, Loader2, Plus, Undo2 } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import { confirmarEdicoes, useEdicaoPendente } from '@/lib/edicao-pendente';
import { formatarContagem, formatarHora } from '@/lib/format';
import { useRecordShortcuts } from './use-record-shortcuts';

export interface RecordToolbarProps {
  /** Ha alteracoes nao salvas. */
  dirty: boolean;
  /** Salvando agora (desabilita Salvar, mostra "Salvando…"). */
  saving?: boolean;
  /** Ultimo salvamento bem-sucedido ("Salvo às HH:MM"). */
  savedAt?: Date | null;
  /** Mensagem do ultimo erro ao salvar (mostra "Não foi salvo"). */
  erro?: string | null;
  /** Registro novo ainda nao gravado: o contador vira "Novo registro". */
  novo?: boolean;
  /** Posicao 1-based e total para "2 de 10.294". */
  index?: number;
  total?: number;
  canPrev: boolean;
  canNext: boolean;
  /** Quantidade de campos alterados ("2 campos alterados"). Sem valor, mostra "Alterado". */
  alteradosCount?: number;
  variant?: 'light' | 'dark';
  /**
   * 'registro' (padrao): a barra de um registro (lista, anterior/proximo, novo, menu).
   * 'lote': alteracoes de varias celulas de uma grade. Mostra so "Desfazer tudo" e "Salvar alteracoes" (com a contagem
   * "n alteracoes"); sem navegacao, novo, menu nem atalhos de lista/excluir. Cmd/Ctrl+S salva; Cmd/Ctrl+Z nao e ligado
   * (a grade tem editores de celula com o desfazer nativo).
   */
  modo?: 'registro' | 'lote';
  onList?: () => void;
  onSave: () => void;
  onNew?: () => void;
  onUndo: () => void;
  onPrev?: () => void;
  onNext?: () => void;
  onDelete?: () => void;
  /** false: some o "Excluir" do menu e o atalho (ex.: item vindo do Protheus, que so se arquiva la). Padrao: sim. */
  excluivel?: boolean;
  /** Textos da confirmacao de Excluir (ex.: explicar que o registro e arquivado, nao apagado). */
  excluirTitulo?: string;
  excluirDescricao?: ReactNode;
  excluirConfirmar?: string;
  /** Rotulo do item do menu ⋯ (padrao 'Excluir'; ex.: 'Desativar usuário' quando o registro nao e apagado). */
  excluirRotulo?: string;
  /** Itens opcionais do menu ⋯. Sem handler, o item aparece desabilitado (como no design). */
  onDuplicate?: () => void;
  onPrint?: () => void;
  onReports?: () => void;
  onHistory?: () => void;
  /**
   * true (padrao): `position: fixed` no rodape da viewport (portal no body). false: inline no fluxo, so para a vitrine
   * de componentes, onde varias barras aparecem empilhadas.
   */
  fixa?: boolean;
  className?: string;
}

const BOTAO =
  'inline-flex size-9 items-center justify-center rounded-lg text-inherit transition-colors hover:bg-line-soft disabled:pointer-events-none disabled:opacity-40 focus-visible:outline-2 focus-visible:outline-ring';

function Separador({ escuro }: { escuro: boolean }) {
  return <span aria-hidden="true" className={cn('mx-1 h-[22px] w-px', escuro ? 'bg-white/15' : 'bg-border')} />;
}

function ItemMenu({
  children,
  onSelect,
  disabled,
  atalho,
  perigo = false,
}: {
  children: ReactNode;
  onSelect?: () => void;
  disabled?: boolean;
  atalho?: string;
  perigo?: boolean;
}) {
  return (
    <DropdownMenuItem
      disabled={disabled}
      onSelect={onSelect}
      className={cn(
        'h-[34px] cursor-pointer rounded-sm px-2.5 text-small',
        perigo && 'text-danger-ink focus:bg-danger-bg focus:text-danger-ink',
      )}
    >
      {children}
      {atalho && <DropdownMenuShortcut className="font-mono text-[11px] tracking-normal text-ink-muted">{atalho}</DropdownMenuShortcut>}
    </DropdownMenuItem>
  );
}

/**
 * Barra de registro flutuante: `position: fixed` no rodape da viewport, via portal no body (nenhum ancestral com
 * overflow/transform a prende), centralizada na area de conteudo (desconta o menu lateral pela CSS var `--menu-w`, a partir de `lg`, e o painel do copiloto pela `--copiloto-w`).
 * Enquanto montada, a barra liga `data-barra-registro` no body e o conteudo ganha padding inferior (globals.css). Presentacional: estados vem por props.
 * Atalhos (⌘S ⌘N ⌘[ ⌘] ⌘Z ⌘⌫ Esc; Ctrl no lugar de ⌘) ficam ativos enquanto a barra esta montada.
 * Atalhos do menu (Duplicar, Imprimir) nao sao exibidos: nao ha atalho ligado para eles.
 */
export function RecordToolbar({
  dirty,
  saving = false,
  savedAt = null,
  erro = null,
  novo = false,
  index,
  total,
  canPrev,
  canNext,
  alteradosCount,
  variant = 'light',
  modo = 'registro',
  onList,
  onSave,
  onNew,
  onUndo,
  onPrev,
  onNext,
  onDelete,
  excluivel = true,
  excluirTitulo = 'Excluir este registro?',
  excluirDescricao = 'O registro será excluído. Confirme para continuar.',
  excluirConfirmar = 'Excluir',
  excluirRotulo = 'Excluir',
  onDuplicate,
  onPrint,
  onReports,
  onHistory,
  fixa = true,
  className,
}: RecordToolbarProps) {
  const escuro = variant === 'dark';
  const lote = modo === 'lote';
  const [excluirAberto, setExcluirAberto] = useState(false);
  // Celula com texto digitado e ainda nao confirmado (blur/Enter/Tab) conta como alteracao, e Salvar a confirma antes de gravar.
  const pendente = useEdicaoPendente();
  const sujo = dirty || pendente;
  const podeSalvar = sujo && !saving;
  const salvarAtual = useRef(onSave);
  salvarAtual.current = onSave;
  const salvarConfirmando = useCallback(() => {
    confirmarEdicoes(); // a tela re-renderiza com o valor digitado; salvarAtual ja aponta para o salvar novo
    salvarAtual.current();
  }, []);

  // Enquanto a barra existe, o conteudo reserva espaco inferior (globals.css: body[data-barra-registro] #conteudo).
  useEffect(() => {
    if (!fixa) return;
    const n = Number(document.body.dataset.barraRegistro ?? '0') + 1;
    document.body.dataset.barraRegistro = String(n);
    return () => {
      const resto = Number(document.body.dataset.barraRegistro ?? '1') - 1;
      if (resto <= 0) {
        delete document.body.dataset.barraRegistro;
        document.body.style.removeProperty('--barra-registro-h');
      } else document.body.dataset.barraRegistro = String(resto);
    };
  }, [fixa]);

  // A altura real da barra (ela quebra em telas estreitas) vira --barra-registro-h: 16px = o bottom-4 + a folga do conteudo.
  const refBarra = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = refBarra.current;
    if (!fixa || !el) return;
    const aplicar = () => document.body.style.setProperty('--barra-registro-h', `${el.getBoundingClientRect().height + 16}px`);
    aplicar();
    if (typeof ResizeObserver === 'undefined') return;
    const observador = new ResizeObserver(aplicar);
    observador.observe(el);
    return () => observador.disconnect();
  }, [fixa]);

  useRecordShortcuts({
    dirty,
    onSave: podeSalvar ? salvarConfirmando : undefined,
    onNew: !lote && !dirty && !saving ? onNew : undefined,
    onPrev: !lote && canPrev ? onPrev : undefined,
    onNext: !lote && canNext ? onNext : undefined,
    onUndo: !lote && dirty ? onUndo : undefined,
    onList: lote ? undefined : onList,
    onDelete: lote || !excluivel ? undefined : () => setExcluirAberto(true),
  });

  const textoAlterado =
    alteradosCount === undefined
      ? 'Alterado'
      : lote
        ? `${alteradosCount} ${alteradosCount === 1 ? 'alteração' : 'alterações'}`
        : `${alteradosCount} ${alteradosCount === 1 ? 'campo alterado' : 'campos alterados'}`;

  const contador = novo
    ? 'Novo registro'
    : index !== undefined && total !== undefined
      ? `${formatarContagem(index)} de ${formatarContagem(total)}`
      : null;

  // Regiao viva persistente (montada desde o inicio): leitores de tela so anunciam mudancas de texto dentro dela.
  let status: ReactNode = null;
  let corStatus = escuro ? 'text-line-strong' : 'text-ink-muted';
  if (erro) {
    status = <span title={erro}>Não foi salvo: {erro}</span>;
    corStatus = cn('font-medium', escuro ? 'text-warning-ink-dark' : 'text-danger-ink');
  } else if (saving) {
    status = <span className="sr-only">Salvando…</span>; // o botao mostra "Salvando…" em tela
  } else if (sujo) {
    status = (
      <span className="flex items-center gap-1.5">
        <span aria-hidden="true" className={cn('size-[7px] rounded-full', escuro ? 'bg-warning-dot-dark' : 'bg-warning-dot')} />
        {textoAlterado}
      </span>
    );
    corStatus = escuro ? 'text-warning-ink-dark' : 'text-warning-ink';
  } else if (savedAt) {
    status = <span>Salvo às {formatarHora(savedAt)}</span>;
  }

  const botaoSalvar = (
    <button
      type="button"
      onClick={salvarConfirmando}
      disabled={!podeSalvar}
      aria-busy={saving || undefined}
      className={cn(
        'inline-flex h-9 items-center gap-2 rounded-lg px-3.5 text-small font-semibold transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring disabled:pointer-events-none',
        escuro
          ? 'bg-background text-foreground hover:bg-background/90 disabled:opacity-40'
          : sujo || saving
            ? 'bg-primary text-primary-foreground hover:bg-primary/90'
            : 'bg-border text-ink-muted',
      )}
    >
      {saving ? (
        <>
          <Loader2 aria-hidden="true" className="size-4 animate-spin" />
          Salvando…
        </>
      ) : (
        <>
          {lote ? 'Salvar alterações' : 'Salvar'}
          {!escuro && podeSalvar && <span className="font-mono text-[11px] opacity-80">⌘S</span>}
        </>
      )}
    </button>
  );

  const barra = (
    <div
      ref={refBarra}
      data-testid={fixa ? 'record-toolbar-fixa' : undefined}
      className={cn(fixa ? 'pointer-events-none fixed bottom-4 left-0 right-[var(--copiloto-w,0px)] z-40 flex justify-center px-3 md:left-[var(--menu-w,17.5rem)]' : 'flex justify-center', className)}
    >
      <div
        role="toolbar"
        aria-label={lote ? 'Alterações da lista' : 'Registro'}
        className={cn(
          'pointer-events-auto inline-flex w-fit max-w-full flex-wrap items-center justify-center gap-1 rounded-3xl p-1.5 shadow-float',
          escuro ? 'border border-white/[0.08] bg-toolbar-dark text-line-soft' : 'border border-border bg-white/90 text-[#27272a]',
        )}
      >
        {!lote && (
          <>
        <button type="button" aria-label="Lista" title="Lista (Esc)" onClick={onList} className={BOTAO}>
          <List aria-hidden="true" className="size-[17px]" />
        </button>
        <Separador escuro={escuro} />

        <button
          type="button"
          aria-label="Anterior"
          title={canPrev ? 'Anterior (⌘[)' : 'Primeiro registro'}
          onClick={onPrev}
          disabled={!canPrev}
          className={BOTAO}
        >
          <ChevronLeft aria-hidden="true" className="size-4" />
        </button>
        {contador && (
          <span className={cn('px-1.5 font-mono text-caption', escuro ? 'text-line-strong' : 'text-ink-secondary')}>
            {contador}
          </span>
        )}
        <button
          type="button"
          aria-label="Próximo"
          title={canNext ? 'Próximo (⌘])' : 'Último registro'}
          onClick={onNext}
          disabled={!canNext}
          className={BOTAO}
        >
          <ChevronRight aria-hidden="true" className="size-4" />
        </button>
        <Separador escuro={escuro} />
          </>
        )}

        {lote ? (
          <button
            type="button"
            aria-label="Desfazer tudo"
            title="Desfazer tudo"
            onClick={onUndo}
            disabled={!dirty || saving}
            className={cn(BOTAO, 'w-auto gap-1.5 px-2.5 text-small')}
          >
            <Undo2 aria-hidden="true" className="size-4" />
            Desfazer tudo
          </button>
        ) : dirty ? (
          <button type="button" aria-label="Desfazer" title="Desfazer (⌘Z)" onClick={onUndo} className={BOTAO}>
            <Undo2 aria-hidden="true" className="size-4" />
          </button>
        ) : (
          <button type="button" aria-label="Novo" title="Novo (⌘N)" onClick={onNew} disabled={saving} className={BOTAO}>
            <Plus aria-hidden="true" className="size-[17px]" />
          </button>
        )}
        <span role="status" aria-live={erro ? 'assertive' : 'polite'} className={cn('text-caption', status && 'px-2', corStatus)}>
          {status}
        </span>
        {botaoSalvar}

        {!lote && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button type="button" aria-label="Mais ações" className={cn(BOTAO, 'data-[state=open]:bg-line-soft')}>
              <EllipsisVertical aria-hidden="true" className="size-4" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" sideOffset={10} className="w-[260px] rounded-xl border-border bg-popover/95 p-1.5 shadow-menu">
            <ItemMenu disabled={!onDuplicate} onSelect={onDuplicate}>
              Duplicar
            </ItemMenu>
            <ItemMenu disabled={!onPrint} onSelect={onPrint}>
              Imprimir ficha
            </ItemMenu>
            <ItemMenu disabled={!onReports} onSelect={onReports}>
              Relatórios
            </ItemMenu>
            <ItemMenu disabled={!onHistory} onSelect={onHistory}>
              Histórico de alterações
            </ItemMenu>
            {excluivel && (
              <>
                <DropdownMenuSeparator className="mx-1 my-1.5" />
                <ItemMenu perigo onSelect={() => setExcluirAberto(true)} atalho="⌘⌫">
                  {excluirRotulo}
                </ItemMenu>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
        )}
      </div>

      <AlertDialog open={excluirAberto} onOpenChange={setExcluirAberto}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{excluirTitulo}</AlertDialogTitle>
            <AlertDialogDescription>{excluirDescricao}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={onDelete}
              className="bg-destructive text-white hover:bg-destructive/90"
            >
              {excluirConfirmar}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
  return fixa ? createPortal(barra, document.body) : barra;
}
